import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { JSONParser } from "../common/function";
import {
  GET_PERMISSION_CATALOG_URL,
  GET_ROLES_URL,
  GET_USER_PERMISSIONS_URL,
  UPDATE_USER_PERMISSIONS_URL,
  UPDATE_USER_ROLE_URL,
  GET_PERMISSION_SETS_URL,
  APPLY_PERMISSION_SET_URL,
} from "../common/url";
import type {
  ApiResponse,
  PermissionCatalogEntry,
  Role,
  Permission,
  PermissionData,
  PermissionSet,
} from "../types/api";

/**
 * Get all available permissions from catalog
 */
export const getPermissionCatalog = async (): Promise<ApiResponse<PermissionCatalogEntry[]>> => {
  const config = {
    method: "GET",
    url: GET_PERMISSION_CATALOG_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error", data: [] });
  } catch (error) {
    console.error("Failed to get permission catalog:", error);
    throw error;
  }
};

/**
 * Get all roles
 */
export const getRoles = async (): Promise<ApiResponse<Role[]>> => {
  const config = {
    method: "GET",
    url: GET_ROLES_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error", data: [] });
  } catch (error) {
    console.error("Failed to get roles:", error);
    throw error;
  }
};

/**
 * Get user's effective permissions
 */
export const getUserPermissions = async (
  userId: number | string
): Promise<ApiResponse<Permission[]>> => {
  const config = {
    method: "GET",
    url: `${GET_USER_PERMISSIONS_URL}/${userId}`,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error", data: [] });
  } catch (error) {
    console.error("Failed to get user permissions:", error);
    throw error;
  }
};

/**
 * Get user's full permission data
 */
export const getUserFullPermissions = async (
  userId: number | string
): Promise<ApiResponse<PermissionData>> => {
  const config = {
    method: "GET",
    url: `${GET_USER_PERMISSIONS_URL}/${userId}/full`,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    const parsed = JSONParser(decrypted, {
      status: "Error" as const,
      data: { permissions: [], permissionKeys: [] } as PermissionData,
    });
    return parsed;
  } catch (error) {
    console.error("Failed to get user full permissions:", error);
    throw error;
  }
};

/**
 * Update user's role
 */
export const updateUserRole = async (
  userId: number | string,
  roleId: number | string,
  updatedBy: number | string
): Promise<ApiResponse<void>> => {
  const config = {
    method: "PUT",
    url: UPDATE_USER_ROLE_URL,
    headers: authHeader(),
    data: { userId, roleId, updatedBy },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to update user role:", error);
    throw error;
  }
};

/**
 * Update user's permissions (bulk)
 */
export const updateUserPermissions = async (
  userId: number | string,
  permissionKeys: string[],
  updatedBy: number | string
): Promise<ApiResponse<void>> => {
  const config = {
    method: "PUT",
    url: UPDATE_USER_PERMISSIONS_URL,
    headers: authHeader(),
    data: { userId, permissionKeys, updatedBy },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to update user permissions:", error);
    throw error;
  }
};

/**
 * Get permission sets
 */
export const getPermissionSets = async (): Promise<ApiResponse<PermissionSet[]>> => {
  const config = {
    method: "GET",
    url: GET_PERMISSION_SETS_URL,
    headers: authHeader(),
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error", data: [] });
  } catch (error) {
    console.error("Failed to get permission sets:", error);
    throw error;
  }
};

/**
 * Apply permission set to user
 */
export const applyPermissionSet = async (
  userId: number | string,
  setId: number | string,
  grantedBy: number | string
): Promise<ApiResponse<void>> => {
  const config = {
    method: "POST",
    url: APPLY_PERMISSION_SET_URL,
    headers: authHeader(),
    data: { userId, setId, grantedBy },
  };

  try {
    const response = await API(config);
    const decrypted = response.data;
    return JSONParser(decrypted, { status: "Error" });
  } catch (error) {
    console.error("Failed to apply permission set:", error);
    throw error;
  }
};
